import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import Modal from '../common/Modal';
import MiniLoader from '../common/MiniLoader';
import { PACKAGE_PRICES, CONTRACTS } from '../../config/contracts';
import { useContracts } from '../../hooks/useContract';
import { useUsdtBalance } from '../../hooks/useUsdtBalance';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/errors';
import { formatUsdt } from '../../utils/format';
import { getPackageColor } from '../../config/packageColors';

// Retopup an ALREADY-ACTIVE package. ceiling += 5*price, invested += price, earned preserved.
// One-click approve + retopup.
const RetopupModal = ({ packageId, userId, onClose, onDone }) => {
  const { logic, storage, usdt } = useContracts();
  const { balance, allowance, refresh: refreshBalance } = useUsdtBalance();
  const notify = useNotify();

  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState('');
  const [pkgSnapshot, setPkgSnapshot] = useState(null);
  const runIdRef = useRef(0);

  const price = PACKAGE_PRICES[packageId - 1];
  const priceBn = ethers.utils.parseUnits(String(price), 18);
  const needsApproval = allowance.lt(priceBn);
  const insufficientBalance = balance.lt(priceBn);
  const color = getPackageColor(packageId);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!userId) return;
      const myRun = ++runIdRef.current;
      try {
        const p = await storage.userPackages(userId, packageId);
        if (cancelled || myRun !== runIdRef.current) return;
        setPkgSnapshot({
          totalInvested: p[2],
          totalEarned: p[3],
          ceilingLimit: p[4]
        });
      } catch (_) { /* ignore */ }
    };
    load();
    return () => { cancelled = true; };
  }, [userId, packageId, storage]);

  const retopup = async () => {
    setBusy(true);
    try {
      if (needsApproval) {
        setStep('approving');
        const l = notify.loading('Approving USDT (one-time)...');
        try {
          const tx = await usdt.approve(CONTRACTS.GlobalStorage, ethers.constants.MaxUint256);
          notify.dismiss(l);
          const m = notify.loading('Confirming approval...');
          await tx.wait();
          notify.dismiss(m);
          await refreshBalance();
        } catch (err) {
          notify.dismiss(l);
          notify.error(parseError(err));
          setBusy(false); setStep('');
          return;
        }
      }

      setStep('retopping');
      const l = notify.loading(`Retopping up Package ${packageId}...`);
      try {
        const tx = await logic.retopupPackage(packageId);
        notify.dismiss(l);
        const m = notify.loading('Confirming...');
        await tx.wait();
        notify.dismiss(m);
        notify.success(`Package ${packageId} retopped up — ceiling extended by $${price * 5}`);
        if (onDone) onDone();
      } catch (err) {
        notify.dismiss(l);
        notify.error(parseError(err));
      }
    } finally {
      setBusy(false);
      setStep('');
    }
  };

  const newCeiling = pkgSnapshot ? pkgSnapshot.ceilingLimit.add(priceBn.mul(5)) : null;

  return (
    <Modal isOpen={true} onClose={busy ? undefined : onClose} title={`Retopup Package ${packageId}`}>
      <div className="text-center mb-lg">
        <div
          className="package-price"
          style={{
            fontSize: 48,
            background: `linear-gradient(135deg, ${color.from}, ${color.to})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}
        >
          ${price}
        </div>
        <div className="text-muted mt-xs" style={{ letterSpacing: 2, fontSize: 11 }}>
          RETOPUP AMOUNT
        </div>
      </div>

      {pkgSnapshot && (
        <div className="card" style={{ padding: 16, marginBottom: 12 }}>
          <div className="text-muted mb-sm" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
            Ceiling Change
          </div>
          <div className="flex flex-between mb-sm">
            <span style={{ fontSize: 13 }}>Current ceiling</span>
            <span style={{ fontSize: 14, fontWeight: 600 }}>{formatUsdt(pkgSnapshot.ceilingLimit)} USDT</span>
          </div>
          <div className="flex flex-between mb-sm">
            <span style={{ fontSize: 13 }}>Already earned</span>
            <span style={{ fontSize: 14, fontWeight: 600 }} className="text-cyan">
              {formatUsdt(pkgSnapshot.totalEarned)} USDT
            </span>
          </div>
          <div className="divider-gradient" style={{ margin: '10px 0' }}></div>
          <div className="flex flex-between">
            <span style={{ fontSize: 13 }}>New ceiling after retopup</span>
            <span
              style={{
                fontSize: 15,
                fontWeight: 700,
                background: `linear-gradient(135deg, ${color.from}, ${color.to})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              +${price * 5} USDT
            </span>
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 14, marginBottom: 16 }}>
        <div className="flex flex-between">
          <span className="text-muted" style={{ fontSize: 13 }}>Your USDT Balance</span>
          <span style={{ fontSize: 14, fontWeight: 600 }} className={insufficientBalance ? 'text-error' : ''}>
            {formatUsdt(balance)} USDT
          </span>
        </div>
      </div>

      {insufficientBalance && (
        <div className="mb-md">
          <span className="badge badge-error">Insufficient USDT balance</span>
        </div>
      )}

      <div className="bucket-note" style={{ marginTop: 0, marginBottom: 14 }}>
        💡 Retopup extends this package's ceiling by <strong>${price * 5}</strong> and preserves your earned amount.
        Full distribution fires (40% direct + 30% level + 30% royalty).
      </div>

      <div className="flex gap-sm">
        <button className="btn btn-secondary" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button
          className="btn btn-primary btn-lg flex-1"
          onClick={retopup}
          disabled={busy || insufficientBalance}
          style={{ fontSize: 15, padding: '16px 22px' }}
        >
          {busy ? (
            <>
              <MiniLoader />
              {step === 'approving' ? 'Approving...' : step === 'retopping' ? 'Retopping...' : 'Processing...'}
            </>
          ) : (
            <>🔁 Retopup for ${price}</>
          )}
        </button>
      </div>
    </Modal>
  );
};

export default RetopupModal;
